import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingCart, faRss, faSpider, faCheckCircle, faCircle,
    faSpinner, faLock, faRocket, faBolt, faShieldAlt, faInfoCircle,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { getApiRootUrl } from '../services/api';
import type {
    DataSourceDetectionResult,
    DataSourceRecommendation,
    DataSourceFeedFormat
} from '../types/dataSources';

interface DataSourceSelectorProps {
    websiteUrl: string;
    onMethodSelected: (method: string, config?: any) => void;
    onSkip?: () => void;
    autoDetect?: boolean; // Auto-run detection on mount
    className?: string;
}

const DataSourceSelector: React.FC<DataSourceSelectorProps> = ({
    websiteUrl,
    onMethodSelected,
    onSkip,
    autoDetect = true,
    className = ''
}) => {
    const [detecting, setDetecting] = useState<boolean>(false);
    const [detectionResult, setDetectionResult] = useState<DataSourceDetectionResult | null>(null);
    const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [manualFeedFormVisible, setManualFeedFormVisible] = useState<boolean>(false);
    const [manualFeedUrl, setManualFeedUrl] = useState<string>('');
    const [manualFeedFormat, setManualFeedFormat] = useState<DataSourceFeedFormat>('google_shopping');
    const [manualFeedAutoSync, setManualFeedAutoSync] = useState<boolean>(true);
    const [manualFeedFrequency, setManualFeedFrequency] = useState<string>('6');
    const [manualFeedError, setManualFeedError] = useState<string | null>(null);
    const [manualFeedProcessing, setManualFeedProcessing] = useState<boolean>(false);
    const [shopifyFormVisible, setShopifyFormVisible] = useState<boolean>(false);
    const [shopifyDomain, setShopifyDomain] = useState<string>('');
    const [shopifyAccessToken, setShopifyAccessToken] = useState<string>('');
    const [shopifyApiVersion, setShopifyApiVersion] = useState<string>('2024-01');
    const [shopifyAutoSync, setShopifyAutoSync] = useState<boolean>(true);
    const [shopifyFrequency, setShopifyFrequency] = useState<string>('6');
    const [shopifyError, setShopifyError] = useState<string | null>(null);
    const [shopifyProcessing, setShopifyProcessing] = useState<boolean>(false);
    const [wooFormVisible, setWooFormVisible] = useState<boolean>(false);
    const [wooStoreUrl, setWooStoreUrl] = useState<string>('');
    const [wooConsumerKey, setWooConsumerKey] = useState<string>('');
    const [wooConsumerSecret, setWooConsumerSecret] = useState<string>('');
    const [wooAutoSync, setWooAutoSync] = useState<boolean>(true);
    const [wooFrequency, setWooFrequency] = useState<string>('6');
    const [wooError, setWooError] = useState<string | null>(null);
    const [wooProcessing, setWooProcessing] = useState<boolean>(false);

    useEffect(() => {
        closeAllForms();
        setSelectedMethod(null);
        if (autoDetect && websiteUrl && !detectionResult && !detecting) {
            handleDetect();
        }
    }, [websiteUrl, autoDetect]);

    const handleDetect = async () => {
        if (!websiteUrl) {
            toast.error('Please enter a website URL first');
            return;
        }

        setDetecting(true);
        setError(null);

        try {
            const API_BASE_URL = getApiRootUrl();
            const response = await fetch(`${API_BASE_URL}/api/client/detect-data-sources`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ website_url: websiteUrl }),
            });

            if (!response.ok) {
                throw new Error('Failed to detect data sources');
            }

            const result = await response.json();

            if (result.success && result.data) {
                setDetectionResult(result.data);

                // Show detection result
                if (result.data.platform) {
                    toast.success(`Detected ${result.data.platform} store!`, {
                        autoClose: 3000
                    });
                } else if (result.data.feed_url) {
                    toast.success('Product feed found!', {
                        autoClose: 3000
                    });
                }
            } else {
                throw new Error('Invalid response from server');
            }
        } catch (err: any) {
            console.error('Detection error:', err);
            setError(err.message || 'Failed to detect data sources');
            toast.error('Could not detect data sources. You can still use web scraping.');

            // Set fallback recommendation
            setDetectionResult({
                platform: null,
                platform_confidence: 0,
                feed_url: null,
                feed_format: null,
                recommendations: [
                    {
                        method: 'feed',
                        title: 'Product Feed (Manual)',
                        description: 'Provide a feed URL to import structured product data.',
                        priority: 2,
                        requires_auth: false,
                        benefits: ['Structured data', 'Supports scheduled re-sync'],
                        manual_entry: true
                    },
                    {
                        method: 'scraping',
                        title: 'Web Scraping',
                        description: 'Works for any website',
                        priority: 3,
                        requires_auth: false,
                        benefits: ['Works for any site', 'No setup required']
                    }
                ]
            });
        } finally {
            setDetecting(false);
        }
    };

    const getWebsiteHostname = () => {
        if (!websiteUrl) return '';
        try {
            return new URL(websiteUrl).hostname.toLowerCase();
        } catch {
            return websiteUrl.replace(/^https?:\/\//i, '').split('/')[0].toLowerCase();
        }
    };

    const getWebsiteUrl = () => {
        if (!websiteUrl) return '';
        const trimmed = websiteUrl.trim();
        if (!trimmed) return '';
        if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
            return trimmed;
        }
        return `https://${trimmed}`;
    };

    const resetManualFeedState = () => {
        setManualFeedFormVisible(false);
        setManualFeedUrl('');
        setManualFeedFormat('google_shopping');
        setManualFeedAutoSync(true);
        setManualFeedFrequency('6');
        setManualFeedError(null);
        setManualFeedProcessing(false);
    };

    const resetShopifyState = () => {
        setShopifyFormVisible(false);
        setShopifyDomain('');
        setShopifyAccessToken('');
        setShopifyApiVersion('2024-01');
        setShopifyAutoSync(true);
        setShopifyFrequency('6');
        setShopifyError(null);
        setShopifyProcessing(false);
    };

    const resetWooState = () => {
        setWooFormVisible(false);
        setWooStoreUrl('');
        setWooConsumerKey('');
        setWooConsumerSecret('');
        setWooAutoSync(true);
        setWooFrequency('6');
        setWooError(null);
        setWooProcessing(false);
    };

    const closeAllForms = () => {
        resetManualFeedState();
        resetShopifyState();
        resetWooState();
    };

    const handleSelectMethod = (method: string, recommendation: DataSourceRecommendation) => {
        const config: any = {
            source_type: method,
        };

        if (method === 'feed') {
            closeAllForms();
            setSelectedMethod(method);

            let feedUrl = recommendation.feed_url;

            if (!feedUrl || recommendation.manual_entry) {
                setManualFeedFormVisible(true);
                setManualFeedError(null);
                setManualFeedProcessing(false);
                setManualFeedUrl(feedUrl || detectionResult?.feed_url || '');
                setManualFeedFormat(
                    recommendation.feed_format
                        ? recommendation.feed_format
                        : detectionResult?.feed_format ?? 'google_shopping'
                );
                setManualFeedAutoSync(true);
                setManualFeedFrequency('6');
                return;
            }

            config.feed_url = feedUrl;
            config.feed_format = recommendation.feed_format || detectionResult?.feed_format || 'google_shopping';

            if (recommendation.manual_entry || !recommendation.feed_url) {
                config.manual_feed = true;
            }
        } else if (method === 'shopify_api') {
            closeAllForms();
            setSelectedMethod(method);
            setShopifyFormVisible(true);
            setShopifyError(null);
            setShopifyProcessing(false);
            const inferred = getWebsiteHostname();
            setShopifyDomain(inferred || '');
            setShopifyAccessToken('');
            setShopifyApiVersion('2024-01');
            setShopifyAutoSync(true);
            setShopifyFrequency('6');
            return;
        } else if (method === 'woocommerce_api') {
            closeAllForms();
            setSelectedMethod(method);
            setWooFormVisible(true);
            setWooError(null);
            setWooProcessing(false);
            const inferredUrl = getWebsiteUrl();
            setWooStoreUrl(inferredUrl);
            setWooConsumerKey('');
            setWooConsumerSecret('');
            setWooAutoSync(true);
            setWooFrequency('6');
            return;
        }

        if (['shopify_api', 'woocommerce_api', 'magento_api', 'bigcommerce_api'].includes(method)) {
            config.platform = detectionResult?.platform;
            config.requires_credentials = true;
        }

        closeAllForms();
        setSelectedMethod(method);
        onMethodSelected(method, config);
    };

    const handleManualFeedSubmit = async () => {
        if (!manualFeedUrl.trim()) {
            setManualFeedError('Feed URL is required');
            return;
        }

        let normalizedUrl = manualFeedUrl.trim();
        if (!/^https?:\/\//i.test(normalizedUrl)) {
            normalizedUrl = `https://${normalizedUrl}`;
        }

        try {
            // Validate URL structure
            new URL(normalizedUrl);
        } catch {
            setManualFeedError('Enter a valid feed URL (including domain and path)');
            return;
        }

        setManualFeedProcessing(true);
        setManualFeedError(null);

        const config: any = {
            source_type: 'feed',
            feed_url: normalizedUrl,
            feed_format: manualFeedFormat || 'google_shopping',
            manual_feed: true,
        };

        if (manualFeedAutoSync) {
            config.auto_sync_enabled = true;
            config.sync_frequency_hours = Number(manualFeedFrequency) || 6;
        } else {
            config.auto_sync_enabled = false;
        }

        console.log('[DataSourceSelector] Submitting feed config:', config);

        try {
            await Promise.resolve(onMethodSelected('feed', config));
            console.log('[DataSourceSelector] Feed submission successful, resetting state');
            resetManualFeedState();
        } catch (err: any) {
            console.error('[DataSourceSelector] Manual feed submission failed:', err);
            const message =
                err?.message ||
                err?.response?.data?.detail ||
                'Failed to save feed. Please try again.';
            setManualFeedError(message);
        } finally {
            setManualFeedProcessing(false);
        }
    };

    const handleManualFeedCancel = () => {
        closeAllForms();
        setSelectedMethod(null);
    };

    const handleShopifySubmit = async () => {
        const domain = shopifyDomain.trim();
        const token = shopifyAccessToken.trim();

        if (!domain || !token) {
            setShopifyError('Shop domain and access token are required');
            return;
        }

        const normalizedDomain = domain.replace(/^https?:\/\//i, '').split('/')[0].toLowerCase();

        setShopifyProcessing(true);
        setShopifyError(null);

        const config = {
            source_type: 'shopify_api',
            shop_domain: normalizedDomain,
            access_token: token,
            api_version: shopifyApiVersion?.trim() || undefined,
            auto_sync_enabled: shopifyAutoSync,
            sync_frequency_hours: Number(shopifyFrequency) || 6,
            inline_submitted: true
        };

        try {
            await Promise.resolve(onMethodSelected('shopify_api', config));
            resetShopifyState();
        } catch (err: any) {
            console.error('Shopify submission failed:', err);
            let message = err?.message || err?.response?.data?.detail || 'Failed to connect Shopify store';

            // Provide more helpful error messages for common issues
            if (message.includes('401') || message.toLowerCase().includes('unauthorized')) {
                message = 'Invalid access token or insufficient permissions';
            } else if (message.toLowerCase().includes('invalid') && message.toLowerCase().includes('credentials')) {
                message = 'Invalid Shopify credentials - please verify your access token';
            } else if (message.toLowerCase().includes('expired')) {
                message = 'Access token has expired - please generate a new one';
            }

            setShopifyError(message);
            // Don't reset the form so user can fix the issue
            setShopifyProcessing(false);
            return; // Important: return early to prevent form reset
        }

        // Only reset processing if successful (catch block returns early)
        setShopifyProcessing(false);
    };

    const handleShopifyCancel = () => {
        resetShopifyState();
        setSelectedMethod(null);
    };

    const handleWooSubmit = async () => {
        const url = wooStoreUrl.trim();
        const key = wooConsumerKey.trim();
        const secret = wooConsumerSecret.trim();

        if (!url || !key || !secret) {
            setWooError('Store URL, consumer key, and consumer secret are required');
            return;
        }

        const normalizedUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;

        setWooProcessing(true);
        setWooError(null);

        const config = {
            source_type: 'woocommerce_api',
            store_url: normalizedUrl,
            consumer_key: key,
            consumer_secret: secret,
            auto_sync_enabled: wooAutoSync,
            sync_frequency_hours: Number(wooFrequency) || 6,
            inline_submitted: true
        };

        try {
            await Promise.resolve(onMethodSelected('woocommerce_api', config));
            resetWooState();
        } catch (err: any) {
            console.error('WooCommerce submission failed:', err);
            let message = err?.message || err?.response?.data?.detail || 'Failed to connect WooCommerce store';

            // Provide more helpful error messages for common issues
            if (message.includes('401') || message.toLowerCase().includes('unauthorized')) {
                message = 'Invalid consumer key or secret - please verify your credentials';
            } else if (message.toLowerCase().includes('invalid') && message.toLowerCase().includes('credentials')) {
                message = 'Invalid WooCommerce credentials - please check your consumer key and secret';
            } else if (message.toLowerCase().includes('expired')) {
                message = 'Consumer credentials have expired - please regenerate them in WooCommerce';
            }

            setWooError(message);
            // Don't reset the form so user can fix the issue
            setWooProcessing(false);
            return; // Important: return early to prevent form reset
        }

        // Only reset processing if successful (catch block returns early)
        setWooProcessing(false);
    };

    const handleWooCancel = () => {
        resetWooState();
        setSelectedMethod(null);
    };

    const getMethodIcon = (method: string) => {
        switch (method) {
            case 'shopify_api':
            case 'woocommerce_api':
            case 'magento_api':
            case 'bigcommerce_api':
                return faShoppingCart;
            case 'feed':
                return faRss;
            case 'scraping':
                return faSpider;
            default:
                return faCircle;
        }
    };

    const getPriorityBadge = (priority: number) => {
        switch (priority) {
            case 1:
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <FontAwesomeIcon icon={faBolt} className="mr-1" />
                        Recommended
                    </span>
                );
            case 2:
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <FontAwesomeIcon icon={faRocket} className="mr-1" />
                        Fast
                    </span>
                );
            case 3:
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Fallback
                    </span>
                );
            default:
                return null;
        }
    };

    if (detecting) {
        return (
            <div className={`bg-white rounded-xl p-8 shadow-lg border border-gray-200 ${className}`}>
                <div className="text-center">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        spin
                        className="text-4xl text-blue-500 mb-4"
                    />
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        Analyzing Your Website
                    </h3>
                    <p className="text-gray-600">
                        Detecting platform and available data sources...
                    </p>
                </div>
            </div>
        );
    }

    if (!detectionResult) {
        return (
            <div className={`bg-white rounded-xl p-8 shadow-lg border border-gray-200 ${className}`}>
                <div className="text-center">
                    <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="text-4xl text-gray-400 mb-4"
                    />
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        Ready to Detect Data Sources
                    </h3>
                    <p className="text-gray-600 mb-6">
                        Click the button below to analyze your website
                    </p>
                    <button
                        onClick={handleDetect}
                        className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                        <FontAwesomeIcon icon={faSpider} className="mr-2" />
                        Detect Data Sources
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={`bg-white rounded-xl p-8 shadow-lg border border-gray-200 ${className}`}>
            {/* Header */}
            <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    Choose Your Data Source
                </h3>
                <p className="text-gray-600">
                    {detectionResult.platform
                        ? `We detected a ${detectionResult.platform} store. Choose the best method to import your products.`
                        : 'Select how you want to import your data.'
                    }
                </p>
            </div>

            {/* Platform Detection Info */}
            {detectionResult.platform && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 text-xl mt-0.5 mr-3" />
                        <div>
                            <h4 className="font-semibold text-green-900 mb-1">
                                Platform Detected: {detectionResult.platform.charAt(0).toUpperCase() + detectionResult.platform.slice(1)}
                            </h4>
                            <p className="text-sm text-green-700">
                                Confidence: {Math.round(detectionResult.platform_confidence * 100)}%
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Feed Detection Info */}
            {detectionResult.feed_url && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start">
                        <FontAwesomeIcon icon={faRss} className="text-blue-600 text-xl mt-0.5 mr-3" />
                        <div className="flex-1">
                            <h4 className="font-semibold text-blue-900 mb-1">
                                Product Feed Found
                            </h4>
                            <p className="text-sm text-blue-700 break-all">
                                {detectionResult.feed_url}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Method Options */}
            <div className="space-y-4">
                {detectionResult.recommendations.map((recommendation, index) => (
                    <div
                        key={index}
                        onClick={() => handleSelectMethod(recommendation.method, recommendation)}
                        className={`
                            relative p-5 rounded-xl border-2 cursor-pointer transition-all
                            ${selectedMethod === recommendation.method
                                ? 'border-blue-500 bg-blue-50 shadow-lg'
                                : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                            }
                        `}
                    >
                        <div className="flex items-start">
                            {/* Icon */}
                            <div className={`
                                flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center mr-4
                                ${selectedMethod === recommendation.method
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 text-gray-600'
                                }
                            `}>
                                <FontAwesomeIcon
                                    icon={getMethodIcon(recommendation.method)}
                                    className="text-xl"
                                />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-lg font-semibold text-gray-800 flex items-center">
                                        {recommendation.title}
                                        {recommendation.requires_auth && (
                                            <FontAwesomeIcon
                                                icon={faLock}
                                                className="ml-2 text-sm text-gray-500"
                                                title="Requires authentication"
                                            />
                                        )}
                                    </h4>
                                    {getPriorityBadge(recommendation.priority)}
                                </div>

                                <p className="text-sm text-gray-600 mb-3">
                                    {recommendation.description}
                                </p>

                                {recommendation.method === 'feed' && !recommendation.feed_url && (
                                    <p className="text-xs text-blue-600 mb-3">
                                        Provide your feed URL after selecting this option to enable scheduled feed imports.
                                    </p>
                                )}

                                {recommendation.method === 'feed' && recommendation.manual_entry && selectedMethod === recommendation.method && manualFeedFormVisible && (
                                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                        <h5 className="text-sm font-semibold text-blue-900 mb-3">
                                            Add Your Product Feed URL
                                        </h5>

                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-xs font-semibold text-blue-800 mb-1" htmlFor="manual-feed-url">
                                                    Feed URL
                                                </label>
                                                <input
                                                    id="manual-feed-url"
                                                    type="text"
                                                    value={manualFeedUrl}
                                                    onChange={(e) => setManualFeedUrl(e.target.value)}
                                                    placeholder="https://example.com/products-feed.xml"
                                                    className="w-full px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                                />
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-xs font-semibold text-blue-800 mb-1">
                                                        Feed Format
                                                    </label>
                                                    <select
                                                        value={manualFeedFormat}
                                                        onChange={(e) => setManualFeedFormat(e.target.value as DataSourceFeedFormat)}
                                                        className="w-full px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                                    >
                                                        <option value="google_shopping">Google Shopping (XML)</option>
                                                        <option value="rss">RSS / Atom</option>
                                                        <option value="json">JSON</option>
                                                        <option value="csv">CSV</option>
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-semibold text-blue-800 mb-1">
                                                        Auto Re-sync
                                                    </label>
                                                    <div className="flex items-center space-x-2">
                                                        <input
                                                            id="manual-feed-autosync"
                                                            type="checkbox"
                                                            checked={manualFeedAutoSync}
                                                            onChange={(e) => setManualFeedAutoSync(e.target.checked)}
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-blue-300 rounded"
                                                        />
                                                        <label htmlFor="manual-feed-autosync" className="text-xs text-blue-800">
                                                            Re-import feed automatically
                                                        </label>
                                                    </div>

                                                    {manualFeedAutoSync && (
                                                        <select
                                                            value={manualFeedFrequency}
                                                            onChange={(e) => setManualFeedFrequency(e.target.value)}
                                                            className="mt-2 w-full px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                                        >
                                                            <option value="3">Every 3 hours</option>
                                                            <option value="6">Every 6 hours</option>
                                                            <option value="12">Every 12 hours</option>
                                                            <option value="24">Every 24 hours</option>
                                                        </select>
                                                    )}
                                                </div>
                                            </div>

                                            {manualFeedError && (
                                                <div className="p-2 bg-red-100 border border-red-200 text-xs text-red-700 rounded-lg">
                                                    {manualFeedError}
                                                </div>
                                            )}

                                            <div className="flex items-center justify-end space-x-2 pt-2">
                                                <button
                                                    type="button"
                                                    onClick={handleManualFeedCancel}
                                                    className="px-3 py-2 text-xs font-semibold text-blue-700 hover:text-blue-900 transition-colors"
                                                    disabled={manualFeedProcessing}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleManualFeedSubmit}
                                                    className={`px-4 py-2 text-xs font-semibold text-white rounded-lg shadow-md transition-colors ${
                                                        manualFeedProcessing
                                                            ? 'bg-blue-300 cursor-not-allowed'
                                                            : 'bg-blue-600 hover:bg-blue-700'
                                                    }`}
                                                    disabled={manualFeedProcessing}
                                                >
                                                    {manualFeedProcessing ? 'Saving…' : 'Save Feed & Continue'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {recommendation.method === 'shopify_api' && selectedMethod === recommendation.method && shopifyFormVisible && (
                                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                        <h5 className="text-sm font-semibold text-blue-900 mb-3">
                                            Connect Your Shopify Store
                                        </h5>

                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-xs font-semibold text-blue-800 mb-1" htmlFor="shopify-domain">
                                                    Shop Domain
                                                </label>
                                                <input
                                                    id="shopify-domain"
                                                    type="text"
                                                    value={shopifyDomain}
                                                    onChange={(e) => setShopifyDomain(e.target.value)}
                                                    placeholder="your-store.myshopify.com"
                                                    className="w-full px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-semibold text-blue-800 mb-1" htmlFor="shopify-token">
                                                    Admin API Access Token
                                                </label>
                                                <input
                                                    id="shopify-token"
                                                    type="password"
                                                    value={shopifyAccessToken}
                                                    onChange={(e) => setShopifyAccessToken(e.target.value)}
                                                    placeholder="shpat_..."
                                                    className="w-full px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                                />
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-xs font-semibold text-blue-800 mb-1">
                                                        API Version (optional)
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={shopifyApiVersion}
                                                        onChange={(e) => setShopifyApiVersion(e.target.value)}
                                                        placeholder="2024-01"
                                                        className="w-full px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-blue-800 mb-1">
                                                        Auto Re-sync
                                                    </label>
                                                    <div className="flex items-center space-x-2">
                                                        <input
                                                            id="shopify-autosync"
                                                            type="checkbox"
                                                            checked={shopifyAutoSync}
                                                            onChange={(e) => setShopifyAutoSync(e.target.checked)}
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-blue-300 rounded"
                                                        />
                                                        <label htmlFor="shopify-autosync" className="text-xs text-blue-800">
                                                            Re-sync automatically
                                                        </label>
                                                    </div>
                                                    {shopifyAutoSync && (
                                                        <select
                                                            value={shopifyFrequency}
                                                            onChange={(e) => setShopifyFrequency(e.target.value)}
                                                            className="mt-2 w-full px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                                        >
                                                            <option value="3">Every 3 hours</option>
                                                            <option value="6">Every 6 hours</option>
                                                            <option value="12">Every 12 hours</option>
                                                            <option value="24">Every 24 hours</option>
                                                        </select>
                                                    )}
                                                </div>
                                            </div>

                                            {shopifyError && (
                                                <div className="p-2 bg-red-100 border border-red-200 text-xs text-red-700 rounded-lg">
                                                    <div className="flex items-start">
                                                        <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 mt-0.5 text-red-600" />
                                                        <div className="flex-1">
                                                            <p className="font-semibold">{shopifyError}</p>
                                                            {shopifyError.includes('401') || shopifyError.toLowerCase().includes('invalid') || shopifyError.toLowerCase().includes('expired') ? (
                                                                <p className="text-xs mt-1">Please check your access token and try again.</p>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                        <div className="flex items-center justify-end space-x-2 pt-2">
                                                <button
                                                    type="button"
                                                    onClick={handleShopifyCancel}
                                                    className="px-3 py-2 text-xs font-semibold text-blue-700 hover:text-blue-900 transition-colors"
                                                    disabled={shopifyProcessing}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleShopifySubmit}
                                                    className={`px-4 py-2 text-xs font-semibold text-white rounded-lg shadow-md transition-colors ${
                                                        shopifyProcessing
                                                            ? 'bg-blue-300 cursor-not-allowed'
                                                            : 'bg-blue-600 hover:bg-blue-700'
                                                    }`}
                                                    disabled={shopifyProcessing}
                                                >
                                                    {shopifyProcessing ? 'Connecting…' : 'Connect & Import'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {recommendation.method === 'woocommerce_api' && selectedMethod === recommendation.method && wooFormVisible && (
                                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                        <h5 className="text-sm font-semibold text-blue-900 mb-3">
                                            Connect Your WooCommerce Store
                                        </h5>

                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-xs font-semibold text-blue-800 mb-1" htmlFor="woo-url">
                                                    Store URL
                                                </label>
                                                <input
                                                    id="woo-url"
                                                    type="text"
                                                    value={wooStoreUrl}
                                                    onChange={(e) => setWooStoreUrl(e.target.value)}
                                                    placeholder="https://yourstore.com"
                                                    className="w-full px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                                />
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-xs font-semibold text-blue-800 mb-1" htmlFor="woo-key">
                                                        Consumer Key
                                                    </label>
                                                    <input
                                                        id="woo-key"
                                                        type="text"
                                                        value={wooConsumerKey}
                                                        onChange={(e) => setWooConsumerKey(e.target.value)}
                                                        className="w-full px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-blue-800 mb-1" htmlFor="woo-secret">
                                                        Consumer Secret
                                                    </label>
                                                    <input
                                                        id="woo-secret"
                                                        type="password"
                                                        value={wooConsumerSecret}
                                                        onChange={(e) => setWooConsumerSecret(e.target.value)}
                                                        className="w-full px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-xs font-semibold text-blue-800 mb-1">
                                                    Auto Re-sync
                                                </label>
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        id="woo-autosync"
                                                        type="checkbox"
                                                        checked={wooAutoSync}
                                                        onChange={(e) => setWooAutoSync(e.target.checked)}
                                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-blue-300 rounded"
                                                    />
                                                    <label htmlFor="woo-autosync" className="text-xs text-blue-800">
                                                        Re-sync automatically
                                                    </label>
                                                </div>
                                                {wooAutoSync && (
                                                    <select
                                                        value={wooFrequency}
                                                        onChange={(e) => setWooFrequency(e.target.value)}
                                                        className="mt-2 w-full px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                                    >
                                                        <option value="3">Every 3 hours</option>
                                                        <option value="6">Every 6 hours</option>
                                                        <option value="12">Every 12 hours</option>
                                                        <option value="24">Every 24 hours</option>
                                                    </select>
                                                )}
                                            </div>

                                            {wooError && (
                                                <div className="p-2 bg-red-100 border border-red-200 text-xs text-red-700 rounded-lg">
                                                    <div className="flex items-start">
                                                        <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 mt-0.5 text-red-600" />
                                                        <div className="flex-1">
                                                            <p className="font-semibold">{wooError}</p>
                                                            {wooError.includes('401') || wooError.toLowerCase().includes('invalid') || wooError.toLowerCase().includes('expired') ? (
                                                                <p className="text-xs mt-1">Please verify your API credentials in WooCommerce settings.</p>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="flex items-center justify-end space-x-2 pt-2">
                                                <button
                                                    type="button"
                                                    onClick={handleWooCancel}
                                                    className="px-3 py-2 text-xs font-semibold text-blue-700 hover:text-blue-900 transition-colors"
                                                    disabled={wooProcessing}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleWooSubmit}
                                                    className={`px-4 py-2 text-xs font-semibold text-white rounded-lg shadow-md transition-colors ${
                                                        wooProcessing
                                                            ? 'bg-blue-300 cursor-not-allowed'
                                                            : 'bg-blue-600 hover:bg-blue-700'
                                                    }`}
                                                    disabled={wooProcessing}
                                                >
                                                    {wooProcessing ? 'Connecting…' : 'Connect & Import'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Benefits */}
                                {recommendation.benefits && recommendation.benefits.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {recommendation.benefits.map((benefit, i) => (
                                            <span
                                                key={i}
                                                className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-white border border-gray-200 text-gray-700"
                                            >
                                                <FontAwesomeIcon icon={faCheckCircle} className="mr-1 text-green-500" />
                                                {benefit}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Auth Notice */}
                                {recommendation.requires_auth && selectedMethod === recommendation.method && (
                                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                        <p className="text-sm text-amber-800">
                                            <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
                                            You'll need to provide API credentials in the next step
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Selection Indicator */}
                            {selectedMethod === recommendation.method && (
                                <div className="flex-shrink-0 ml-4">
                                    <FontAwesomeIcon
                                        icon={faCheckCircle}
                                        className="text-2xl text-blue-500"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Error Message */}
            {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{error}</p>
                </div>
            )}

            {/* Action Buttons */}
            <div className="mt-6 flex items-center justify-between">
                {onSkip && (
                    <button
                        onClick={onSkip}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
                    >
                        Skip for now
                    </button>
                )}

                <button
                    onClick={handleDetect}
                    disabled={detecting}
                    className="px-4 py-2 text-blue-600 hover:text-blue-800 font-medium transition-colors disabled:opacity-50"
                >
                    <FontAwesomeIcon icon={faSpinner} spin={detecting} className="mr-2" />
                    Re-detect
                </button>
            </div>
        </div>
    );
};

export default DataSourceSelector;
